import React, { useEffect, useCallback } from 'react';
import { Modal, Form, Input, InputNumber, Select, Button } from 'antd';
import type { ListBuilderHotel, ListBuilderCity } from '../../types/listBuilder';
import { listBuilderEn } from '../../content/listBuilder.en';

function parseCommaList(s: string | undefined): string[] {
  if (!s || typeof s !== 'string') return [];
  return s.split(',').map((x) => x.trim()).filter(Boolean);
}
function formatCommaList(arr: string[] | undefined): string {
  return Array.isArray(arr) ? arr.join(', ') : '';
}
/** Coerce form value to number; InputNumber may submit string when user types. */
function toNum(x: unknown): number | undefined {
  if (typeof x === 'number' && !Number.isNaN(x)) return x;
  if (x === null || x === undefined || x === '') return undefined;
  const n = Number(x);
  return Number.isNaN(n) ? undefined : n;
}

const HotelFormModal: React.FC<{
  open: boolean;
  onClose: () => void;
  onSave: (values: Partial<ListBuilderHotel> & { cityId: string }) => void;
  cities: ListBuilderCity[];
  cityId: string;
  initial?: ListBuilderHotel | null;
}> = ({ open, onClose, onSave, cities, cityId, initial }) => {
  const [form] = Form.useForm();
  useEffect(() => {
    if (open) {
      form.setFieldsValue({
        cityId: initial?.cityId ?? cityId,
        name: initial?.name ?? '',
        description: initial?.description ?? '',
        budgetCategory: initial?.budgetCategory ?? '',
        pricePerNight: initial?.pricePerNight,
        currency: initial?.currency ?? '',
        area: initial?.area ?? '',
        url: initial?.url ?? '',
        amenities: formatCommaList(initial?.amenities),
        rating: initial?.rating,
        familyNotes: initial?.familyNotes ?? '',
        accessibilityNotes: initial?.accessibilityNotes ?? '',
        tags: formatCommaList(initial?.tags),
        latitude: initial?.geocode?.latitude,
        longitude: initial?.geocode?.longitude,
        addressLine1: initial?.address?.line1 ?? '',
        addressLine2: initial?.address?.line2 ?? '',
        addressCity: initial?.address?.city ?? '',
        addressState: initial?.address?.state ?? '',
        addressPostalCode: initial?.address?.postalCode ?? '',
        addressCountry: initial?.address?.country ?? '',
        phoneNumber: initial?.phoneNumber ?? '',
      });
    }
  }, [open, initial, cityId, form]);

  const confirmClose = useCallback(() => {
    if (form.isFieldsTouched()) {
      Modal.confirm({
        title: 'Discard changes?',
        content: 'You have unsaved changes. Are you sure you want to close?',
        okText: 'Discard',
        okType: 'danger',
        cancelText: 'Keep editing',
        onOk: () => { form.resetFields(); onClose(); },
      });
    } else {
      onClose();
    }
  }, [form, onClose]);

  return (
    <Modal
      open={open}
      title={initial ? listBuilderEn.hotels.editHotel : listBuilderEn.hotels.addHotel}
      onCancel={confirmClose}
      footer={null}
      destroyOnClose
      width={560}
    >
      <Form
        form={form}
        layout="vertical"
        onFinish={(v) => {
          const amenities = parseCommaList(v.amenities);
          const tags = parseCommaList(v.tags);
          const lat = toNum(v.latitude);
          const lng = toNum(v.longitude);
          const hasAddress = v.addressLine1 || v.addressLine2 || v.addressCity || v.addressState || v.addressPostalCode || v.addressCountry;
          onSave({
            ...v,
            cityId: v.cityId,
            amenities: amenities.length ? amenities : undefined,
            tags: tags.length ? tags : undefined,
            geocode: lat != null && lng != null ? { latitude: lat, longitude: lng } : null,
            address: hasAddress ? {
              line1: v.addressLine1 || null,
              line2: v.addressLine2 || null,
              city: v.addressCity || null,
              state: v.addressState || null,
              postalCode: v.addressPostalCode || null,
              country: v.addressCountry || null,
            } : null,
            phoneNumber: v.phoneNumber || null,
          });
          form.resetFields();
          onClose();
        }}
      >
        <Form.Item name="cityId" label={listBuilderEn.hotels.form.city} rules={[{ required: true }]}>
          <Select
            placeholder={listBuilderEn.hotels.form.cityPlaceholder}
            options={cities.map((c) => ({ value: c.id, label: `${c.name}, ${c.country}` }))}
            showSearch
            filterOption={(input, opt) => (opt?.label ?? '').toLowerCase().includes(input.toLowerCase())}
          />
        </Form.Item>
        <Form.Item name="name" label={listBuilderEn.hotels.form.name} rules={[{ required: true }]}>
          <Input placeholder={listBuilderEn.hotels.form.namePlaceholder} />
        </Form.Item>
        <Form.Item name="phoneNumber" label={listBuilderEn.address.phoneNumber}>
          <Input placeholder={listBuilderEn.address.phoneNumberPlaceholder} />
        </Form.Item>
        <div style={{ marginBottom: 8, fontWeight: 500 }}>{listBuilderEn.address.sectionTitle}</div>
        <Form.Item name="addressLine1" style={{ marginBottom: 4 }}>
          <Input placeholder={listBuilderEn.address.line1Placeholder} />
        </Form.Item>
        <Form.Item name="addressLine2" style={{ marginBottom: 4 }}>
          <Input placeholder={listBuilderEn.address.line2Placeholder} />
        </Form.Item>
        <div style={{ display: 'flex', gap: 8, marginBottom: 4 }}>
          <Form.Item name="addressCity" style={{ flex: 1, marginBottom: 0 }}>
            <Input placeholder={listBuilderEn.address.cityPlaceholder} />
          </Form.Item>
          <Form.Item name="addressState" style={{ flex: 1, marginBottom: 0 }}>
            <Input placeholder={listBuilderEn.address.statePlaceholder} />
          </Form.Item>
        </div>
        <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
          <Form.Item name="addressPostalCode" style={{ flex: 1, marginBottom: 0 }}>
            <Input placeholder={listBuilderEn.address.postalCodePlaceholder} />
          </Form.Item>
          <Form.Item name="addressCountry" style={{ flex: 1, marginBottom: 0 }}>
            <Input placeholder={listBuilderEn.address.countryPlaceholder} />
          </Form.Item>
        </div>
        <Form.Item name="description" label={listBuilderEn.hotels.form.description}>
          <Input.TextArea rows={2} placeholder={listBuilderEn.hotels.form.descriptionPlaceholder} />
        </Form.Item>
        <Form.Item name="budgetCategory" label={listBuilderEn.hotels.form.budgetCategory}>
          <Input placeholder={listBuilderEn.hotels.form.budgetCategoryPlaceholder} />
        </Form.Item>
        <Form.Item name="pricePerNight" label={listBuilderEn.hotels.form.pricePerNight}>
          <InputNumber min={0} style={{ width: '100%' }} />
        </Form.Item>
        <Form.Item name="currency" label={listBuilderEn.hotels.form.currency}>
          <Input placeholder={listBuilderEn.hotels.form.currencyPlaceholder} />
        </Form.Item>
        <Form.Item name="area" label={listBuilderEn.hotels.form.area}>
          <Input placeholder={listBuilderEn.hotels.form.areaPlaceholder} />
        </Form.Item>
        <Form.Item name="url" label={listBuilderEn.hotels.form.url}>
          <Input placeholder={listBuilderEn.hotels.form.urlPlaceholder} />
        </Form.Item>
        <Form.Item name="amenities" label={listBuilderEn.hotels.form.amenities}>
          <Input placeholder={listBuilderEn.hotels.form.amenitiesPlaceholder} />
        </Form.Item>
        <Form.Item name="rating" label={listBuilderEn.hotels.form.rating}>
          <InputNumber min={0} max={5} step={0.5} style={{ width: '100%' }} />
        </Form.Item>
        <Form.Item name="familyNotes" label={listBuilderEn.hotels.form.familyNotes}>
          <Input.TextArea rows={2} placeholder={listBuilderEn.hotels.form.familyNotesPlaceholder} />
        </Form.Item>
        <Form.Item name="accessibilityNotes" label={listBuilderEn.hotels.form.accessibilityNotes}>
          <Input.TextArea rows={2} placeholder={listBuilderEn.hotels.form.accessibilityNotesPlaceholder} />
        </Form.Item>
        <Form.Item name="tags" label={listBuilderEn.hotels.form.tags}>
          <Input placeholder={listBuilderEn.hotels.form.tagsPlaceholder} />
        </Form.Item>
        <Form.Item label={listBuilderEn.hotels.form.latitude} style={{ marginBottom: 8 }}>
          <Form.Item name="latitude" noStyle>
            <InputNumber placeholder="e.g. 48.86" style={{ width: '100%' }} />
          </Form.Item>
        </Form.Item>
        <Form.Item name="longitude" label={listBuilderEn.hotels.form.longitude}>
          <InputNumber placeholder="e.g. 2.34" style={{ width: '100%' }} />
        </Form.Item>
        <Form.Item style={{ marginBottom: 0 }}>
          <Button type="primary" htmlType="submit">{listBuilderEn.buttons.save}</Button>
          <Button onClick={confirmClose} style={{ marginLeft: 8 }}>{listBuilderEn.buttons.cancel}</Button>
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default HotelFormModal;
